import type { LayerState, ProjectSchemaJSON } from '@takeshape/schema';
import { createRoutine } from 'app/common/utils/new-routines';
import type { ProjectSchemaCompat } from 'app/compat/types';
import type { WrappedGraphQLError } from 'app/graphql-client.js';
import { type ActionType, createAction } from 'typesafe-actions';
import type { DevelopmentBranch, MergeBranchMutationInput, SchemaUpdate } from './api.js';
import { NAME } from './constants.js';

// ==================== Routine Types ====================

// Post Schema types
export type PostSchemaRoutineMeta = {
  delay?: number;
  branch?: string;
  runtime?: boolean;
};

export type PostSchemaRoutineFulfilledPayload = {
  userSchema: ProjectSchemaCompat;
  runtimeSchema: ProjectSchemaCompat;
};

// Fetch Schema types
export type FetchSchemaMode = 'user' | 'runtime' | 'all';

export type FetchSchemaRoutineMeta = {
  branch?: string;
  runtime?: boolean;
  mode?: FetchSchemaMode;
};

export type FetchSchemaRoutinePayload = {
  userSchema?: ProjectSchemaCompat;
  runtimeSchema?: ProjectSchemaCompat;
} | null;

// Fetch Service Layers types
export type FetchServiceLayersMeta = {
  branch?: string;
};

export type FetchServiceLayersPayload = {
  getAllServiceLayers: {
    servicesHash: string;
    layers: LayerState[];
  };
};

// Update Schema types
export type UpdateSchemaResponse = {
  userSchema: ProjectSchemaCompat;
  runtimeSchema: ProjectSchemaCompat;
};

export type UpdateSchemaMeta = {
  operation: 'add' | 'update' | 'remove';
  shapeId?: string;
};

// Ensure Scratch Branch types
type EnsureScratchBranchAction = {
  name?: string;
};

// ==================== Routines ====================

export const postSchemaRoutine = createRoutine(`${NAME}/POST_SCHEMA`)<
  PostSchemaRoutineMeta,
  ProjectSchemaCompat,
  undefined,
  PostSchemaRoutineFulfilledPayload,
  WrappedGraphQLError,
  undefined
>();

export const fetchSchemaRoutine = createRoutine('branches/FETCH_SCHEMA')<
  FetchSchemaRoutineMeta,
  undefined,
  undefined,
  FetchSchemaRoutinePayload,
  string,
  undefined
>();

export const fetchServiceLayersRoutine = createRoutine('branches/FETCH_SERVICE_LAYERS')<
  FetchServiceLayersMeta,
  undefined,
  undefined,
  FetchServiceLayersPayload,
  string,
  undefined
>();

export const updateSchema = createRoutine('branches/UPDATE_SCHEMA')<
  UpdateSchemaMeta | undefined,
  SchemaUpdate,
  undefined,
  UpdateSchemaResponse,
  string,
  undefined
>();

export const createBranchRoutine = createRoutine('branches/CREATE_BRANCH')<
  { name: string },
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export const deleteBranchRoutine = createRoutine('branches/DELETE_BRANCH')<
  { name: string },
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export const getBranchListRoutine = createRoutine('branches/GET_BRANCH_LIST')<
  undefined,
  undefined,
  undefined,
  { branches: DevelopmentBranch[] | null },
  string,
  undefined
>();

export const mergeBranchRoutine = createRoutine('branches/MERGE_BRANCH')<
  MergeBranchMutationInput,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export const ensureScratchBranchRoutine = createRoutine('branches/ENSURE_SCRATCH_BRANCH')<
  EnsureScratchBranchAction,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

// ==================== Actions ====================

export type SetCurrentBranchPayload = {
  branch: string;
  message?: string;
  snackbarMessage?: boolean;
};

export type SetWorkingCopyPayload = {
  data: ProjectSchemaCompat | null;
  dataOrigin: string | null;
};

export const resetBranchState = createAction(`${NAME}/RESET_BRANCHES`)();
export const setCurrentBranch = createAction(`${NAME}/SET_CURRENT_BRANCH`)<SetCurrentBranchPayload>();
export const setUseScratchForPreview = createAction(`${NAME}/SET_USE_SCRATCH_FOR_PREVIEW`)<boolean>();
export const setUserSchema = createAction(`${NAME}/SET_USER_SCHEMA`)<{
  branch?: string;
  schema: ProjectSchemaCompat;
}>();
export const setProjectSchema = createAction(`${NAME}/SET_PROJECT_SCHEMA`)<{
  branch?: string;
  schema: ProjectSchemaCompat;
}>();
export const clearScratchState = createAction(`${NAME}/CLEAR_SCRATCH_STATE`)();

export enum BranchMergeOperation {
  Update = 'Update',
  Promote = 'Promote'
}

export type OpenBranchMergePayload = {
  branchName: string;
  operation: BranchMergeOperation;
};

export const openBranchMerge = createAction(`${NAME}/OPEN_BRANCH_MERGE`)<OpenBranchMergePayload>();
export const closeBranchMerge = createAction(`${NAME}/CLOSE_BRANCH_PROMOTION`)();
export const setBranchMergeError = createAction(`${NAME}/SET_BRANCH_PROMOTION_ERROR`)<string>();
export const setBranchMergeCanMerge = createAction(`${NAME}/SET_BRANCH_MERGE_CAN_MERGE`)<ProjectSchemaJSON>();
export const setBranchMergeInProgress = createAction(`${NAME}/SET_BRANCH_MERGE_IN_PROGRESS`)<boolean>();
export const setCurrentBranchNeedsUpdate = createAction(`${NAME}/SET_CURRENT_BRANCH_NEEDS_UPDATE`)<boolean | null>();
export const setCurrentBranchCanPromote = createAction(`${NAME}/SET_CURRENT_BRANCH_CAN_PROMOTE`)<boolean | null>();

export type BranchesAction =
  | ActionType<typeof setCurrentBranch>
  | ActionType<typeof resetBranchState>
  | ActionType<typeof setUseScratchForPreview>
  | ActionType<typeof setUserSchema>
  | ActionType<typeof setProjectSchema>
  | ActionType<typeof updateSchema>
  | ActionType<typeof fetchSchemaRoutine>
  | ActionType<typeof fetchServiceLayersRoutine>
  | ActionType<typeof postSchemaRoutine>
  | ActionType<typeof createBranchRoutine>
  | ActionType<typeof deleteBranchRoutine>
  | ActionType<typeof getBranchListRoutine>
  | ActionType<typeof mergeBranchRoutine>
  | ActionType<typeof openBranchMerge>
  | ActionType<typeof closeBranchMerge>
  | ActionType<typeof setBranchMergeError>
  | ActionType<typeof setBranchMergeCanMerge>
  | ActionType<typeof setBranchMergeInProgress>
  | ActionType<typeof setCurrentBranchNeedsUpdate>
  | ActionType<typeof setCurrentBranchCanPromote>
  | ActionType<typeof clearScratchState>
  | ActionType<typeof ensureScratchBranchRoutine>;
